import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { getAuthHeaders, useAuth } from '../contexts/AuthContext';
import { useCountry } from '../contexts/CountryContext';
import { canEditModule } from '../lib/permissions';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { indiaToday } from '../components/sales/format';
import { Target, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Plus, Upload, Download, RefreshCw, ChevronRight, ChevronDown, Layers, Package, Zap, Filter, AlertCircle, ArrowRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { LoadError, errorMessage } from '../components/LoadError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Status configuration with colors
const STATUS_CONFIG = {
  ON_TRACK: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: CheckCircle, label: 'On Track', gradient: 'from-emerald-400 to-emerald-600', light: 'bg-emerald-100' },
  AT_RISK: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: AlertTriangle, label: 'At Risk', gradient: 'from-amber-400 to-orange-500', light: 'bg-amber-100' },
  CRITICAL: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: TrendingDown, label: 'Critical', gradient: 'from-red-400 to-red-600', light: 'bg-red-100' }
};

export const TargetsPage = () => {
  const { user } = useAuth();
  const canEditTargets = canEditModule(user, 'targets');
  const { country, currency } = useCountry();
  const [treeData, setTreeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(() => indiaToday().slice(0, 7));
  const [platformFilter, setPlatformFilter] = useState('ALL');
  const [expandedBrands, setExpandedBrands] = useState({});
  const importInputRef = useRef(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    month: indiaToday().slice(0, 7),
    platform: 'AMAZON',
    category: 'ALL',
    target_revenue: ''
  });
  // Opening the dialog starts it on the month the page is showing (IST).
  const handleDialogOpenChange = (open) => {
    if (open) setFormData((prev) => ({ ...prev, month: selectedMonth }));
    setDialogOpen(open);
  };
  const [importing, setImporting] = useState(false);
  const [pendingImportFile, setPendingImportFile] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const requestIdRef = useRef(0);

  // Decision Strip Filter: 'ALL' | 'ON_TRACK' | 'AT_RISK' | 'CRITICAL'
  const [statusFilter, setStatusFilter] = useState('ALL');

  const fetchData = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setLoadError(null);
    try {
      const [treeRes, skusRes] = await Promise.all([
        axios.get(`${API}/targets/brand-tree?month=${selectedMonth}&platform_filter=${platformFilter}&country=${country}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/skus?country=${country}`, { headers: getAuthHeaders() })
      ]);
      if (requestId !== requestIdRef.current) return;
      setTreeData(treeRes.data);

      const uniqueCategories = [...new Set(skusRes.data.map(s => s.category).filter(Boolean))];
      setCategories(uniqueCategories);
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      // Never show the previous month's numbers after a failed refetch
      setTreeData(null);
      setLoadError(error);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [selectedMonth, platformFilter, country]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleBrand = (brand) => {
    setExpandedBrands(prev => ({ ...prev, [brand]: !prev[brand] }));
  };

  const expandAll = () => {
    const expanded = {};
    treeData?.brand_tree?.forEach(b => expanded[b.brand] = true);
    setExpandedBrands(expanded);
  };

  const collapseAll = () => setExpandedBrands({});

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/targets`, {
        ...formData,
        target_revenue: parseFloat(formData.target_revenue),
        country
      }, { headers: getAuthHeaders() });
      toast.success('Target saved successfully');
      setDialogOpen(false);
      fetchData();
      setFormData({ ...formData, target_revenue: '' });
    } catch (error) {
      toast.error('Failed to save target');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/targets/template`, { headers: getAuthHeaders() });
      const { file, filename } = response.data;
      const blob = new Blob([Uint8Array.from(atob(file), c => c.charCodeAt(0))], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const bytes = new Uint8Array(event.target.result);
      let binary = '';
      for (let i = 0; i < bytes.length; i += 0x8000) {
        binary += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
      }
      resolve(btoa(binary));
    };
    reader.onerror = () => reject(new Error('Could not read the selected file'));
    reader.readAsArrayBuffer(file);
  });

  // Step 1: pick a file -> ask for confirmation (import replaces target values)
  const handleImport = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setPendingImportFile(file);
  };

  // Step 2: confirmed -> read the file, post it, and keep "Importing..." until it settles
  const confirmImport = async () => {
    const file = pendingImportFile;
    if (!file) return;
    setImporting(true);
    try {
      const base64 = await readFileAsBase64(file);
      const response = await axios.post(`${API}/targets/import`,
        { file: base64, country },
        { headers: getAuthHeaders() }
      );
      toast.success(response.data?.message || 'Targets imported');
      const rowErrors = response.data?.errors || [];
      if (rowErrors.length > 0) {
        toast.warning(`${rowErrors.length} row(s) skipped: ${rowErrors.slice(0, 3).join('; ')}${rowErrors.length > 3 ? '…' : ''}`);
      }
      setPendingImportFile(null);
      fetchData();
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to import targets'));
      throw error;
    } finally {
      setImporting(false);
    }
  };

  const getStatusConfig = (status) => STATUS_CONFIG[status] || STATUS_CONFIG.ON_TRACK;

  const formatCurrency = (val) => {
    if (val >= 1000000) return `₹${(val / 1000000).toFixed(1)}M`;
    if (val >= 1000) return `₹${(val / 1000).toFixed(0)}K`;
    return `₹${val.toFixed(0)}`;
  };

  // Filter brands based on decision strip
  const filteredBrands = treeData?.brand_tree?.filter(brand => {
    if (statusFilter === 'ALL') return true;
    return brand.status === statusFilter;
  }) || [];

  // Count stats for decision strip
  const brandStats = {
    total: treeData?.brand_tree?.length || 0,
    onTrack: treeData?.brand_tree?.filter(b => b.status === 'ON_TRACK').length || 0,
    atRisk: treeData?.brand_tree?.filter(b => b.status === 'AT_RISK').length || 0,
    critical: treeData?.brand_tree?.filter(b => b.status === 'CRITICAL').length || 0
  };

  const STATUS_PILLS = [
    { key: 'ALL', label: 'All', count: brandStats.total, icon: null, active: 'bg-gray-800 text-white shadow-md', idle: 'bg-white text-gray-600 hover:bg-gray-100' },
    { key: 'ON_TRACK', label: 'On Track', count: brandStats.onTrack, icon: CheckCircle, active: 'bg-emerald-600 text-white shadow-md', idle: 'bg-white text-emerald-700 hover:bg-emerald-50' },
    { key: 'AT_RISK', label: 'At Risk', count: brandStats.atRisk, icon: AlertTriangle, active: 'bg-amber-500 text-white shadow-md', idle: 'bg-white text-amber-700 hover:bg-amber-50' },
    { key: 'CRITICAL', label: 'Critical', count: brandStats.critical, icon: AlertCircle, active: 'bg-red-500 text-white shadow-md', idle: 'bg-white text-red-700 hover:bg-red-50' }
  ];

  const initialLoading = loading && !treeData && !loadError;
  const summary = treeData?.global_summary;
  const summaryConfig = getStatusConfig(summary?.status);

  return (
    <div className="space-y-6" data-testid="targets-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">Target Achievement</h1>
          <p className="mt-1 text-[14px] lg:text-[15px] text-gray-500">
            {treeData
              ? `${treeData.days_elapsed} of ${treeData.days_in_month} days • ${treeData.days_remaining} days remaining`
              : (loading ? 'Loading targets...' : ' ')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <Input type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} aria-label="Month" className="w-[160px] h-9 text-[13px]" data-testid="month-selector" />
          <Select value={platformFilter} onValueChange={setPlatformFilter}>
            <SelectTrigger className="w-[130px] h-9 text-[13px]" data-testid="platform-filter"><SelectValue placeholder="Platform" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Platforms</SelectItem>
              <SelectItem value="AMAZON">Amazon</SelectItem>
              <SelectItem value="FLIPKART">Flipkart</SelectItem>
              <SelectItem value="FIRSTCRY">Firstcry</SelectItem>
              <SelectItem value="MYNTRA">Myntra</SelectItem>
              <SelectItem value="NYKAA">Nykaa</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handleDownloadTemplate} className="h-9 text-[13px]"><Download className="h-4 w-4 mr-2" />Template</Button>
          {canEditTargets && (
          <>
            <input ref={importInputRef} type="file" accept=".xlsx,.xls" onChange={handleImport} disabled={importing} tabIndex={-1} aria-hidden="true" className="hidden" data-testid="import-targets-input" />
            <Button variant="outline" disabled={importing} onClick={() => importInputRef.current?.click()} className="h-9 text-[13px]" data-testid="import-targets-button"><Upload className="h-4 w-4 mr-2" aria-hidden="true" />{importing ? 'Importing…' : 'Import'}</Button>
          </>
          )}
          <ConfirmDialog
            open={!!pendingImportFile}
            onOpenChange={(open) => { if (!open) setPendingImportFile(null); }}
            title="Import targets?"
            description="Importing replaces the target values for every month, platform and category listed in the file. Targets not in the file are left unchanged."
            confirmLabel="Import and replace"
            busyLabel="Importing..."
            onConfirm={confirmImport}
            testId="targets-import-confirm"
          >
            {pendingImportFile && (
              <p className="text-[13px] text-gray-600">File: <span className="font-medium text-gray-900">{pendingImportFile.name}</span></p>
            )}
          </ConfirmDialog>
          {canEditTargets && (
          <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
            <DialogTrigger asChild>
              <Button className="h-9 px-4 bg-[#007AFF] hover:bg-[#0066CC] text-white text-[13px] font-medium rounded-lg">
                <Plus className="h-4 w-4 mr-2" /> Add Target
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="text-[17px] font-semibold">Set Revenue Target</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[13px]">Month *</Label>
                    <Input type="month" value={formData.month} onChange={(e) => setFormData({...formData, month: e.target.value})} required className="h-9 text-[13px]" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[13px]">Platform *</Label>
                    <Select value={formData.platform} onValueChange={(val) => setFormData({...formData, platform: val})}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="AMAZON">Amazon</SelectItem>
                        <SelectItem value="FLIPKART">Flipkart</SelectItem>
                        <SelectItem value="FIRSTCRY">Firstcry</SelectItem>
                        <SelectItem value="MYNTRA">Myntra</SelectItem>
                        <SelectItem value="NYKAA">Nykaa</SelectItem>
                        <SelectItem value="OTHERS">Others</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-[13px]">Category</Label>
                  <Select value={formData.category} onValueChange={(val) => setFormData({...formData, category: val})}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">All Categories (Platform Total)</SelectItem>
                      {categories.map(cat => (<SelectItem key={cat} value={cat}>{cat}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[13px]">Target Revenue (INR ₹) *</Label>
                  <Input type="number" value={formData.target_revenue} onChange={(e) => setFormData({...formData, target_revenue: e.target.value})} required min="0" step="1000" placeholder="500000" className="h-9 text-[13px]" />
                </div>
                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="h-9 text-[13px]">Cancel</Button>
                  <Button type="submit" className="h-9 bg-[#007AFF] hover:bg-[#0066CC] text-[13px]">Save Target</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          )}
          <Button variant="ghost" size="sm" onClick={fetchData} disabled={loading} aria-label="Refresh targets" className="h-9" data-testid="targets-refresh-button"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></Button>
        </div>
      </div>

      {loadError ? (
        <LoadError what="targets" error={loadError} onRetry={fetchData} retrying={loading} testId="targets-load-error" />
      ) : initialLoading ? (
        <div className="space-y-6" aria-busy="true" data-testid="targets-loading">
          <Skeleton className="h-4 w-full rounded-full" />
          <Skeleton className="h-36 w-full rounded-2xl" />
          <Skeleton className="h-20 w-full rounded-2xl" />
          <Skeleton className="h-64 w-full rounded-2xl" />
        </div>
      ) : (
      <div className={`space-y-6 transition-opacity ${loading ? 'opacity-60 pointer-events-none' : ''}`} aria-busy={loading} data-testid="targets-content">
      {/* ==================== TARGET HEALTH BAR ==================== */}
      {summary && (
        <div className="relative" data-testid="target-health-bar">
          {/* Background Gradient Bar */}
          <div className="h-4 rounded-full bg-gray-200 overflow-hidden shadow-inner">
            <div 
              className={`h-full rounded-full bg-gradient-to-r ${summaryConfig.gradient} transition-all duration-1000 ease-out relative overflow-hidden`}
              style={{ width: `${Math.min(summary.achievement_pct, 100)}%` }}
            >
              {/* Animated shine effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse" />
            </div>
          </div>
          
          {/* Markers */}
          <div className="relative h-8 mt-1">
            {/* Expected marker */}
            <div 
              className="absolute top-0 transform -translate-x-1/2"
              style={{ left: `${treeData?.expected_run_rate_pct || 0}%` }}
            >
              <div className="w-0.5 h-3 bg-gray-400" />
              <span className="text-[10px] text-gray-500 whitespace-nowrap">Expected: {treeData?.expected_run_rate_pct}%</span>
            </div>
            
            {/* Current marker */}
            <div 
              className="absolute top-0 transform -translate-x-1/2"
              style={{ left: `${Math.min(summary.achievement_pct, 100)}%` }}
            >
              <div className={`w-4 h-4 rounded-full bg-gradient-to-br ${summaryConfig.gradient} shadow-lg border-2 border-white -mt-1`} />
              <span className={`text-[11px] font-bold ${summaryConfig.text} whitespace-nowrap mt-1 block`}>
                {summary.achievement_pct}%
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ==================== GLOBAL SUMMARY CARD ==================== */}
      {summary && (
        <Card className={`relative overflow-hidden rounded-2xl border-0 shadow-lg`} data-testid="global-summary">
          {/* Gradient background */}
          <div className={`absolute inset-0 bg-gradient-to-br ${summaryConfig.gradient} opacity-10`} />
          
          <div className="relative p-4 sm:p-6">
            <div className="flex flex-wrap items-center gap-4 sm:gap-6">
              {/* Status Icon */}
              <div className={`hidden sm:flex shrink-0 w-20 h-20 rounded-2xl bg-gradient-to-br ${summaryConfig.gradient} items-center justify-center shadow-lg`}>
                <Target className="h-10 w-10 text-white" />
              </div>
              
              {/* Main Metric */}
              <div className="flex-1 min-w-[200px]">
                <div className="flex flex-wrap items-baseline gap-3">
                  <span className={`text-[40px] sm:text-[56px] font-black tracking-tight ${summaryConfig.text}`}>
                    {summary.achievement_pct}%
                  </span>
                  <span className={`px-3 py-1.5 rounded-full text-[13px] font-bold ${summaryConfig.light} ${summaryConfig.text}`}>
                    {summaryConfig.label}
                  </span>
                </div>
                <p className="text-[14px] text-gray-500 mt-1">
                  {selectedMonth} • {platformFilter === 'ALL' ? 'All Platforms' : platformFilter}
                </p>
              </div>
              
              {/* Key Stats Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6 w-full lg:w-auto">
                <div className="text-center px-4 py-3 bg-white/50 rounded-xl">
                  <p className="text-[11px] text-gray-500 uppercase tracking-wider font-medium">Target</p>
                  <p className="text-[22px] font-bold text-gray-900">{formatCurrency(summary.total_target)}</p>
                </div>
                <div className="text-center px-4 py-3 bg-white/50 rounded-xl">
                  <p className="text-[11px] text-gray-500 uppercase tracking-wider font-medium">Actual</p>
                  <p className="text-[22px] font-bold text-gray-900">{formatCurrency(summary.total_actual)}</p>
                </div>
                <div className="text-center px-4 py-3 bg-white/50 rounded-xl">
                  <p className="text-[11px] text-gray-500 uppercase tracking-wider font-medium">Gap</p>
                  <p className={`text-[22px] font-bold ${summary.gap > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                    {summary.gap > 0 ? '-' : '+'}{formatCurrency(Math.abs(summary.gap))}
                  </p>
                </div>
              </div>
              
              {/* Action Metrics */}
              <div className="flex flex-col gap-2 w-full lg:w-auto pt-3 border-t lg:pt-0 lg:border-t-0 lg:pl-6 lg:border-l border-gray-200">
                <div className="flex items-center gap-2">
                  <Zap className="h-4 w-4 text-orange-500" />
                  <span className="text-[12px] text-gray-500">Daily Run Rate:</span>
                  <span className="text-[14px] font-bold text-orange-700">{formatCurrency(summary.daily_run_rate_needed)}/day</span>
                </div>
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-blue-500" />
                  <span className="text-[12px] text-gray-500">Projected:</span>
                  <span className={`text-[14px] font-bold ${summary.projected_achievement >= 100 ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {summary.projected_achievement}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* ==================== DECISION STRIP ==================== */}
      <Card className="p-4 rounded-2xl border-0 shadow-sm bg-gradient-to-r from-slate-50 to-gray-50" data-testid="decision-strip">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <Filter className="h-5 w-5 text-gray-500" />
            <span className="text-[14px] font-semibold text-gray-700">Quick Filters</span>
            
            {/* Status Filter Pills */}
            <div className="flex flex-wrap gap-2 sm:ml-4" role="group" aria-label="Filter brands by status">
              {STATUS_PILLS.map(({ key, label, count, icon: Icon, active, idle }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setStatusFilter(key)}
                  aria-pressed={statusFilter === key}
                  className={`px-4 py-2 rounded-full text-[12px] font-semibold transition-all flex items-center gap-1.5 ${statusFilter === key ? active : idle}`}
                  data-testid={`status-filter-${key.toLowerCase().replace('_', '-')}`}
                >
                  {Icon && <Icon className="h-3.5 w-3.5" />}
                  {label} ({count})
                </button>
              ))}
            </div>
          </div>
          
          {/* Expand/Collapse */}
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={expandAll} className="h-8 text-[12px]">Expand All</Button>
            <Button variant="outline" size="sm" onClick={collapseAll} className="h-8 text-[12px]">Collapse All</Button>
          </div>
        </div>
        
        {/* Quick Insight */}
        {(brandStats.critical > 0 || brandStats.atRisk > 0) && (
          <div className="mt-4 p-3 rounded-xl bg-gradient-to-r from-red-50 to-amber-50 border border-red-100">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              <span className="text-[13px] font-medium text-gray-700">
                {brandStats.critical > 0 && `${brandStats.critical} brand(s) need immediate attention. `}
                {brandStats.atRisk > 0 && `${brandStats.atRisk} brand(s) at risk of missing target.`}
              </span>
              <ArrowRight className="h-4 w-4 text-gray-400 ml-auto" />
            </div>
          </div>
        )}
      </Card>

      {/* ==================== BRAND TREE TABLE ==================== */}
      {filteredBrands.length > 0 && (
        <Card className="rounded-2xl border-0 shadow-sm overflow-hidden" data-testid="brand-tree">
          <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] text-[13px]">
            <thead>
              <tr className="bg-gray-900 text-white">
                <th className="text-left p-4 font-semibold w-[320px]">Brand / Category</th>
                <th className="text-right p-4 font-semibold">Target</th>
                <th className="text-right p-4 font-semibold">Actual</th>
                <th className="text-right p-4 font-semibold">Gap</th>
                <th className="text-center p-4 font-semibold w-[180px]">Achievement</th>
                <th className="text-right p-4 font-semibold">Run Rate</th>
                <th className="text-center p-4 font-semibold w-[100px]">Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredBrands.map((brand) => {
                const config = getStatusConfig(brand.status);
                const isExpanded = expandedBrands[brand.brand];
                
                return (
                  <React.Fragment key={brand.brand}>
                    {/* ========== BRAND ROW (Level 1) ========== */}
                    <tr 
                      className={`border-b-2 border-gray-100 cursor-pointer transition-all hover:shadow-md ${config.bg}`}
                      onClick={() => toggleBrand(brand.brand)}
                      data-testid={`brand-row-${brand.brand}`}
                    >
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); toggleBrand(brand.brand); }}
                            aria-expanded={!!isExpanded}
                            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${brand.brand} categories`}
                            className={`w-8 h-8 rounded-lg bg-gradient-to-br ${config.gradient} flex items-center justify-center shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2`}
                          >
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4 text-white" aria-hidden="true" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-white" aria-hidden="true" />
                            )}
                          </button>
                          <div>
                            <span className="font-bold text-[15px] text-gray-900">{brand.brand}</span>
                            <span className="ml-2 text-[11px] text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                              {brand.category_count} categories
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-right">
                        <span className="font-bold text-[15px] text-gray-900">{formatCurrency(brand.target)}</span>
                      </td>
                      <td className="p-4 text-right">
                        <span className="font-bold text-[15px] text-gray-900">{formatCurrency(brand.actual)}</span>
                      </td>
                      <td className="p-4 text-right">
                        <span className={`font-bold text-[15px] ${brand.gap > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                          {brand.gap > 0 ? '-' : '+'}{formatCurrency(Math.abs(brand.gap))}
                        </span>
                      </td>
                      <td className="p-4">
                        {/* Modern Achievement Bar */}
                        <div className="flex flex-col items-center gap-1">
                          <span className={`font-black text-[18px] ${config.text}`}>{brand.achievement_pct}%</span>
                          <div className="w-full h-2 rounded-full bg-gray-200 overflow-hidden">
                            <div 
                              className={`h-full rounded-full bg-gradient-to-r ${config.gradient} transition-all duration-500`}
                              style={{ width: `${Math.min(brand.achievement_pct, 100)}%` }}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-right">
                        <span className="font-semibold text-orange-700">
                          {brand.run_rate_needed > 0 ? `${formatCurrency(brand.run_rate_needed)}/day` : '-'}
                        </span>
                      </td>
                      <td className="p-4 text-center">
                        <span className={`px-3 py-1.5 rounded-full text-[11px] font-bold ${config.light} ${config.text} shadow-sm`}>
                          {config.label}
                        </span>
                      </td>
                    </tr>
                    
                    {/* ========== CATEGORY ROWS (Level 2) ========== */}
                    {isExpanded && brand.categories.map((cat) => {
                      const catConfig = getStatusConfig(cat.status);
                      return (
                        <tr 
                          key={`${brand.brand}-${cat.category}`}
                          className="border-b border-gray-100 bg-white hover:bg-gray-50 transition-colors"
                          data-testid={`category-row-${cat.category}`}
                        >
                          <td className="p-4 pl-16">
                            <div className="flex items-center gap-3">
                              <Layers className="h-4 w-4 text-blue-500" />
                              <div className="flex-1">
                                <span className="font-medium text-gray-800">{cat.category}</span>
                                {/* ===== CONTRIBUTION BAR ===== */}
                                <div className="flex items-center gap-2 mt-1">
                                  <div className="w-24 h-1.5 rounded-full bg-gray-200 overflow-hidden">
                                    <div 
                                      className="h-full rounded-full bg-blue-400"
                                      style={{ width: `${Math.min(cat.contribution_pct, 100)}%` }}
                                    />
                                  </div>
                                  <span className="text-[11px] font-semibold text-blue-600">
                                    {cat.contribution_pct}% of brand
                                  </span>
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="p-4 text-right text-gray-700">{formatCurrency(cat.target)}</td>
                          <td className="p-4 text-right text-gray-700">{formatCurrency(cat.actual)}</td>
                          <td className={`p-4 text-right font-medium ${cat.gap > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                            {cat.gap > 0 ? '-' : '+'}{formatCurrency(Math.abs(cat.gap))}
                          </td>
                          <td className="p-4">
                            <div className="flex flex-col items-center gap-1">
                              <span className={`font-bold text-[14px] ${catConfig.text}`}>{cat.achievement_pct}%</span>
                              <div className="w-full h-1.5 rounded-full bg-gray-200 overflow-hidden">
                                <div 
                                  className={`h-full rounded-full bg-gradient-to-r ${catConfig.gradient}`}
                                  style={{ width: `${Math.min(cat.achievement_pct, 100)}%` }}
                                />
                              </div>
                            </div>
                          </td>
                          <td className="p-4 text-right text-[12px] text-orange-700">
                            {cat.run_rate_needed > 0 ? `${formatCurrency(cat.run_rate_needed)}/day` : '-'}
                          </td>
                          <td className="p-4 text-center">
                            <span className={`px-2 py-1 rounded-full text-[10px] font-semibold ${catConfig.light} ${catConfig.text}`}>
                              {catConfig.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          </div>
        </Card>
      )}

      {/* No Data Message */}
      {(!filteredBrands || filteredBrands.length === 0) && (
        <Card className="p-12 rounded-2xl text-center bg-gradient-to-br from-gray-50 to-slate-50">
          <Target className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-[20px] font-bold text-gray-700 mb-2">
            {statusFilter !== 'ALL' ? 'No Matching Brands' : 'No Sales Data'}
          </h3>
          <p className="text-[14px] text-gray-500 mb-4">
            {statusFilter !== 'ALL'
              ? 'No brands match your current filter. Try "All" to see all brands.'
              : `No sales data available for ${selectedMonth}. Add targets and import sales data to see the breakdown.`
            }
          </p>
          {statusFilter !== 'ALL' && (
            <Button variant="outline" onClick={() => setStatusFilter('ALL')} data-testid="status-filter-show-all">
              Show all brands
            </Button>
          )}
          {statusFilter === 'ALL' && canEditTargets && (
            <Button onClick={() => handleDialogOpenChange(true)} className="bg-[#007AFF] hover:bg-[#0066CC]">
              <Plus className="h-4 w-4 mr-2" /> Set Your First Target
            </Button>
          )}
        </Card>
      )}
      </div>
      )}
    </div>
  );
};
